// import getFeatureInfoAtCoordinate from "mobility-toolbox-js/common";
import { unByKey } from "ol/Observable";

import type Layer from "ol/layer/Layer";

// eslint-disable-next-line no-unused-vars
type Callback = (layer: Layer | Layer[]) => void;

/**
 * A layer service class to handle layer adding, removing and visiblity.
 */
export default class LayerService {
  callbacks: Record<string, Callback[]>;
  keys: any[];
  layers: Layer[];
  constructor(layers: Layer[]) {
    this.layers = layers;
    this.callbacks = {};
    this.keys = [];
    this.listenChangeEvt();
  }

  addLayer(layer: Layer) {
    this.layers.push(layer);
  }

  getBaseLayers() {
    return this.getLayersAsFlatArray().filter((l) => {
      return l.get("isBaseLayer");
    });
  }

  // getFeatureInfoAtCoordinate(coordinate, layers) {
  //   const promises = (layers || this.getQueryableLayers()).map((layer) => {
  //     return getFeatureInfoAtCoordinate(coordinate, layer).then(
  //       (featureInfo) => {
  //         return featureInfo;
  //       },
  //     );
  //   });
  //   return Promise.all(promises);
  // }

  getLayer(name: string) {
    return this.getLayersAsFlatArray().find((l) => {
      return l.get("name") === name;
    });
  }

  getLayers(): Layer[] {
    return this.layers;
  }

  getLayersAsFlatArray(optLayers?: Layer[]): Layer[] {
    let layers = [];
    (optLayers || this.getLayers() || []).forEach((l) => {
      layers.push(l);
      const children = l.get("children") || [];
      if (Array.isArray(children)) {
        layers = layers.concat(this.getLayersAsFlatArray(children));
      }
    });
    return layers;
  }

  getParent(child: Layer) {
    return this.getLayersAsFlatArray().find((l) => {
      return !!l.get("children")?.includes(child);
    });
  }

  getParents(child: Layer) {
    let layer = child;
    const parents = [];

    let parentLayer;
    do {
      parentLayer = this.getParent(layer);
      if (parentLayer) {
        parents.push(parentLayer);
        layer = parentLayer;
      }
    } while (parentLayer);

    return parents;
  }

  getQueryableLayers() {
    return this.getLayersAsFlatArray().filter((layer) => {
      return layer.getVisible() && layer.get("isQueryable");
    });
  }

  getRadioGroupLayers(radioGroupName: string) {
    if (radioGroupName) {
      return this.getLayersAsFlatArray().filter((l) => {
        return l.get("radioGroup") === radioGroupName;
      });
    }

    return null;
  }

  listenChangeEvt() {
    if (this.keys) {
      unByKey(this.keys);
      this.keys = [];
    }
    this.getLayersAsFlatArray().forEach((layer) => {
      this.keys.push(
        // @ts-expect-error - coypright property is custom
        layer.on("change:copyright", (evt) => {
          (this.callbacks["change:copyright"] || []).forEach((cb) => {
            return cb(evt.target);
          });
        }),
        layer.on("change:visible", (evt) => {
          const visible = evt.target.getVisible();

          // Apply to siblings only if it's a radio group.
          if (
            // @ts-expect-error - property added dynamically
            !evt.stopPropagationSiblings &&
            layer.get("radioGroup") &&
            visible
          ) {
            const siblings = this.getRadioGroupLayers(
              layer.get("radioGroup"),
            ).filter((l) => {
              return l !== layer;
            });

            siblings.forEach((s) => {
              if (
                visible &&
                s.get("radioGroup") &&
                evt.target.get("radioGroup") === s.get("radioGroup")
              ) {
                s.setVisible(!visible);
              }
            });
          }

          const parent = this.getParent(layer);
          const children = layer.get("children") || [];

          if (visible) {
            // We make the parent visible
            if (parent) {
              parent.setVisible(true);
            }

            // If children doesn't contain any visible layers, we display all children.
            if (
              !children?.some((child) => {
                return child.getVisible();
              })
            ) {
              children.forEach((child) => {
                child.setVisible(true);
              });
            }
          } else {
            // We hide all the children
            children?.forEach((child) => {
              child.setVisible(false);
            });

            // If the parent has no more visible child we also hide it.
            if (
              parent?.getVisible() &&
              !parent?.get("children").find((child) => {
                return child.getVisible();
              })
            ) {
              parent.setVisible(false);
            }
          }

          (this.callbacks["change:visible"] || []).forEach((cb) => {
            return cb(evt.target);
          });
        }),
      );
    });
  }

  on(evt: string, callback: Callback) {
    this.un(evt, callback);
    this.callbacks[evt] = this.callbacks[evt] || [];
    this.callbacks[evt].push(callback);
  }

  setLayers(layers: Layer[]) {
    this.layers = layers;
    this.listenChangeEvt();
    // When we change the layers we trigger an change:layers event
    (this.callbacks["change:layers"] || []).forEach((cb) => {
      return cb(layers);
    });
  }

  un(evt: string, callback: Callback) {
    for (let i = 0; i < (this.callbacks[evt] || []).length; i += 1) {
      if (callback === this.callbacks[evt][i]) {
        this.callbacks[evt].splice(i, 1);
        break;
      }
    }
  }
}
